import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Spinner,
  Alert,
  Button,
  Form,
  InputGroup,
  Row,
  Col,
  Modal,
  Badge,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt,
  faEye,
  faSearch,
  faTimes,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import Pagination from '../common/Pagination';
import RawJsonViewer from '../common/RawJsonViewer';
import useDebounce from '../../hooks/useDebounce';
import {
  fetchSyncRequestLogs,
  fetchSyncRequestLogById,
} from '../../services/syncRequestLogApi';

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 500;

const normalizePayload = (payload) => {
  if (payload == null) return {};
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return payload;
    }
  }
  return payload;
};

const getProviderBadgeVariant = (provider) => {
  if (!provider) return 'secondary';
  const p = String(provider).toLowerCase();
  if (p === 'google') return 'danger';
  if (p === 'apple') return 'dark';
  if (p === 'facebook') return 'primary';
  if (p === 'twitter') return 'info';
  return 'secondary';
};

const SyncRequestLogs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('');
  const [suspicious, setSuspicious] = useState('all'); // 'all' | '1' | '0'
  const [deviceId, setDeviceId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const debouncedEmail = useDebounce(email, SEARCH_DEBOUNCE_MS);
  const debouncedName = useDebounce(name, SEARCH_DEBOUNCE_MS);
  const effectiveSearch = debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : '';
  const effectiveEmail = debouncedEmail.trim();
  const effectiveName = debouncedName.trim();

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailLog, setDetailLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        items_per_page: PER_PAGE,
      };
      if (effectiveEmail) params.email = effectiveEmail;
      if (effectiveName) params.name = effectiveName;
      if (provider) params.provider = provider;
      if (suspicious === '1' || suspicious === '0') {
        params.suspicious = suspicious;
      }
      if (deviceId.trim()) params.device_id = deviceId.trim();
      if (taskId.trim()) params.task_id = taskId.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (effectiveSearch) params.q = effectiveSearch;

      const res = await fetchSyncRequestLogs(params);
      const payload = res.data;
      if (payload?.success && payload?.data) {
        setItems(Array.isArray(payload.data.items) ? payload.data.items : []);
        setTotalItems(payload.data.objects?.total ?? 0);
      } else {
        setItems([]);
        setTotalItems(0);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch sync request logs');
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [effectiveEmail, effectiveName, provider, suspicious, deviceId, taskId, dateFrom, dateTo, effectiveSearch]);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, effectiveEmail, effectiveName, provider, suspicious, deviceId, taskId, dateFrom, dateTo, effectiveSearch]);

  const hasActiveFilters =
    email.trim() ||
    name.trim() ||
    provider ||
    suspicious !== 'all' ||
    deviceId.trim() ||
    taskId.trim() ||
    dateFrom ||
    dateTo ||
    searchTerm.trim();

  const clearFilters = () => {
    setEmail('');
    setName('');
    setProvider('');
    setSuspicious('all');
    setDeviceId('');
    setTaskId('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const formatDate = (val) => {
    if (!val) return '—';
    try {
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? val : d.toLocaleString();
    } catch (_) {
      return val;
    }
  };

  const openDetail = async (id) => {
    setShowDetail(true);
    setDetailLog(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await fetchSyncRequestLogById(id);
      if (res.data?.success && res.data?.data) {
        setDetailLog(res.data.data);
      } else {
        setDetailError(res.data?.message || 'Log not found');
      }
    } catch (err) {
      setDetailError(err.response?.data?.message || err.message || 'Failed to load payload');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setShowDetail(false);
    setDetailLog(null);
    setDetailError(null);
  };

  return (
    <div className="dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
          Sync Request Logs
        </h2>
      </div>
      <p className="text-muted mb-3">
        Raw payloads from mobile <code>POST /songs/sync</code>. Retained for 30 days.
      </p>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Form.Group className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search payload / email / name (min 3 characters)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
            </InputGroup>
          </Form.Group>

          <Row className="g-2 align-items-end">
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Email
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Provider</Form.Label>
              <Form.Select
                className="form-control form-select"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              >
                <option value="">All</option>
                <option value="google">Google</option>
                <option value="apple">Apple</option>
                <option value="facebook">Facebook</option>
                <option value="twitter">Twitter</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Status</Form.Label>
              <Form.Select
                className="form-control form-select"
                value={suspicious}
                onChange={(e) => setSuspicious(e.target.value)}
              >
                <option value="all">All</option>
                <option value="1">Suspicious</option>
                <option value="0">Clean</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Device ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="device_id"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
              />
            </Col>
            <Col xs={12} md={2}>
              <Form.Label className="small mb-1">Task ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="task_id"
                value={taskId}
                onChange={(e) => setTaskId(e.target.value)}
              />
            </Col>
            <Col xs={6} md={1}>
              <Form.Label className="small mb-1">From</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Col>
            <Col xs={6} md={1}>
              <Form.Label className="small mb-1">To</Form.Label>
              <Form.Control
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Col>
            {hasActiveFilters && (
              <Col xs="auto">
                <Button variant="outline-secondary" onClick={clearFilters}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Clear filters
                </Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={PER_PAGE}
            onPageChange={setCurrentPage}
            position="top"
          />

          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="table-vcenter mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Provider</th>
                    <th>Device ID</th>
                    <th>Songs</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No sync request logs found
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>
                          <div>{row.name || '—'}</div>
                          <div className="text-muted small">ID: {row.user_id ?? '—'}</div>
                        </td>
                        <td>{row.email || '—'}</td>
                        <td>
                          <Badge
                            bg={getProviderBadgeVariant(row.provider)}
                            className={
                              row.provider && String(row.provider).toLowerCase() === 'twitter'
                                ? 'text-dark'
                                : 'text-white'
                            }
                          >
                            {row.provider || '—'}
                          </Badge>
                        </td>
                        <td>
                          <code className="small">{row.device_id || '—'}</code>
                        </td>
                        <td>{row.songs_count ?? 0}</td>
                        <td>
                          {row.is_suspicious ? (
                            <Badge bg="warning" text="dark">Suspicious</Badge>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>{formatDate(row.created_at)}</td>
                        <td className="text-end">
                          <Button
                            variant="outline-primary"
                            onClick={() => openDetail(row.id)}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={PER_PAGE}
            onPageChange={setCurrentPage}
            position="bottom"
          />
        </Card.Body>
      </Card>

      <Modal show={showDetail} onHide={closeDetail} size="xl" centered scrollable fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>
            Sync Request Payload
            {detailLog?.id ? ` #${detailLog.id}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" className="mb-3" />
              <p className="mb-0">Loading payload...</p>
            </div>
          ) : detailError ? (
            <Alert variant="danger" className="mb-0">
              {detailError}
            </Alert>
          ) : detailLog ? (
            <>
              <Row className="mb-3 g-2">
                <Col md={3}>
                  <div className="text-muted small">User</div>
                  <div>{detailLog.name || '—'}</div>
                  <div className="text-muted small">ID: {detailLog.user_id ?? '—'}</div>
                </Col>
                <Col md={3}>
                  <div className="text-muted small">Email</div>
                  <div>{detailLog.email || '—'}</div>
                </Col>
                <Col md={2}>
                  <div className="text-muted small">Provider</div>
                  <div>
                    <Badge
                      bg={getProviderBadgeVariant(detailLog.provider)}
                      className={
                        detailLog.provider && String(detailLog.provider).toLowerCase() === 'twitter'
                          ? 'text-dark'
                          : 'text-white'
                      }
                    >
                      {detailLog.provider || '—'}
                    </Badge>
                  </div>
                </Col>
                <Col md={2}>
                  <div className="text-muted small">Device ID</div>
                  <div>
                    <code>{detailLog.device_id || '—'}</code>
                  </div>
                </Col>
                <Col md={2}>
                  <div className="text-muted small">Created</div>
                  <div>{formatDate(detailLog.created_at)}</div>
                </Col>
              </Row>
              <RawJsonViewer data={normalizePayload(detailLog.payload)} maxHeight="65vh" />
            </>
          ) : (
            <div className="bg-light p-3 rounded border text-muted">No payload available</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDetail}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SyncRequestLogs;
